import { useEffect, useRef } from 'react';

/**
 * swagger-ui-react accessibility plugin (issue #688).
 *
 * `swagger-ui-react` renders two elements that ship without an accessible
 * name, tripping axe-core:
 *
 *  - The Servers scheme-selector `<select id="servers">` sits inside a
 *    text-less `<label htmlFor="servers">`, so its accessible name is empty
 *    (axe `select-name`, critical).
 *  - The Authorize dialog's icon-only close button (`.close-modal`) has no
 *    text content (axe `button-name`).
 *
 * The library exposes `wrapComponents` as its supported extension point: each
 * entry wraps a named internal component without forking its render logic.
 * We wrap the two owning components (`Servers`, `authorizationPopup`), render
 * the original inside a `display: contents` passthrough span, and run a cheap
 * idempotent effect that sets `aria-label` on the offending node — scoped to
 * that span's subtree rather than scanning the whole page. The effect is a
 * no-op when the target already has an `aria-label`, so it never clobbers an
 * upstream fix.
 */

/** Props passed through to the wrapped swagger-ui component are opaque to us. */
type PassthroughProps = Record<string, unknown>;

/** swagger-ui's `wrapComponents` contract: `(Original, system) => Component`. */
type WrapComponent = (
  Original: React.ComponentType<PassthroughProps>
) => (props: PassthroughProps) => JSX.Element;

/**
 * Build a wrapper that labels the first element matching `selector` within the
 * wrapped component's own subtree, unless it is already labelled.
 */
function makeA11yWrapper(selector: string, label: string): WrapComponent {
  return (Original) =>
    function SwaggerA11yWrapper(props): JSX.Element {
      const rootRef = useRef<HTMLSpanElement>(null);

      useEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        const target = root.querySelector(selector);
        if (target && !target.hasAttribute('aria-label')) {
          target.setAttribute('aria-label', label);
        }
      });

      return (
        <span className="swagger-a11y-passthrough" ref={rootRef}>
          <Original {...props} />
        </span>
      );
    };
}

/**
 * swagger-ui plugin object. Passed to `<SwaggerUI plugins={[swaggerA11yPlugin]} />`.
 * Exported shape mirrors what the plugin system consumes so unit tests can call
 * `swaggerA11yPlugin.wrapComponents.Servers(Original)` directly.
 */
export const swaggerA11yPlugin: { wrapComponents: Record<string, WrapComponent> } = {
  wrapComponents: {
    Servers: makeA11yWrapper('#servers', 'Servers'),
    authorizationPopup: makeA11yWrapper('.close-modal', 'Close'),
  },
};
